import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { getTitleByAgencyId } from '../../../../../features/state/StateTitle-slice';
import { GetByTitleId } from '../../../../../features/state/StateAgency-Title-slice';
import { getChaptersByTitleId, setChaptersForTitle } from '../../../../../features/state/StateChapter-slice';
import { getAllAgencies } from '../../../../../features/state/StateAgency-slice';
import { Title } from '../../../../../utilities/interfaces/Title';
import { Chapter } from '../../../../../utilities/interfaces/Chapter';
import { Agency } from '../../../../../utilities/interfaces/Agency';
import {AgencyTitle} from "../../../../../utilities/interfaces/AgencyTitles";
import { PDFExport } from '@progress/kendo-react-pdf';
import { chapterAPI, titleAPI, userSubscriptionAPI } from '../../../../../utilities/api';
import { Roles } from '../../../../../utilities/enums/Roles';
import { APISettings } from '../../../../../config/ApiSettings';
import { saveAs } from 'file-saver';
import { emergencyRegulationApi } from '../../../../../utilities/api/emergencyRegulationApi';
import {
  clearUserSubscriptionTableData,
  deleteUserSubscription,
  getUserSubscriptionByEmail, removeUserAgencySubscription,
  removeUserChapterSubscription,
  removeUserTitleSubscription,
} from '../../../../../features/public/publicUserSubscription-slice';
import { useNotificationHelper } from '../../../../../utilities/helpers/NotificationHelper';
import {UserSubscription} from "../../../../../utilities/interfaces/UserSubscription";
import { validateUserRoles } from '../../../../../features/state/StateAuthRole-slice';
import { dictionary } from '../../../../../utilities/types/dictionary';

export const useRulesNavigationController = (agencyId?: any, titleId?: any) => {
  const userSubscriptions: UserSubscription[] = useAppSelector((state) => state.publicUserSubscription.userSubscription);
  const titleValues: Title[] = useAppSelector((state) => state.titles.value);
  const titleList: Title[] = useAppSelector((state) => state.titles.titleList);
  const chapterValues: Chapter[] = useAppSelector((state) => state.chapters.chapters);
  const chapterCache = useAppSelector((state) => state.chapters.chaptersByTitleId);
  const agencyTitleValues: dictionary<AgencyTitle[]> =  useAppSelector((state) => state.agencyTitles.agencyTitles);
  const agencyValues: Agency[] = useAppSelector((state) => state.agency.agencies);

  const [selectedAgency, setSelectedAgency] = useState<any>();
  const [selectedTitle, setSelectedTitle] = useState<any>();
  const [selectedChapter, setSelectedChapter] = useState<any>();
  const [chaptersLoading, setChaptersLoading] = useState<boolean>(false);
  const [titleDialogOpen, setTitleDialogOpen] = useState<boolean>(false);
  const [chapterDialogOpen, setChapterDialogOpen] = useState<boolean>(false);
  const [titleData, setTitleData] = useState<any>(null);
  const pdfExport = useRef<PDFExport>(null);
  const [parentSubscription, setParentSubscription] = useState<any>();
  const [childSubscription, setChildSubscription] = useState<any>();
  const [listOfChildrenSubscriptionsForUserNotification, setListOfChildrenSubscriptionsForUserNotification] = useState<any>([]);

  const subscriptionEmail = localStorage.getItem("subscriptionEmail");
  const subscriptionUserId = localStorage.getItem("subscriptionUserId");
  const subscriptionUser = localStorage.getItem('subscriptionUser');

  const dispatch = useAppDispatch();

  // #region set defaults if provided in URL
  const setDefaultValues = async () => {
    if (agencyValues && agencyId) {
      let index = agencyValues.findIndex(function (agency) {
        return agency.id === agencyId;
      });
      setSelectedAgency(agencyValues[index]);
      await getDefaultTitle();
    }
  };

  const getDefaultTitle = async () => {
    if (titleId && agencyId) {
      let index = titleList.findIndex(function (title) {
        return title.id === titleId;
      });
      setSelectedTitle(titleList[index]);
      dispatch(GetByTitleId(titleId));

      if (chapterCache[titleId]) {
        dispatch(setChaptersForTitle({ titleId, chapters: chapterCache[titleId] }));
      } else {
        dispatch(getChaptersByTitleId(titleId));
      }
    }
  };
  // #endregion

  // #region dropdown functions
  const handleTitleSelectChange = async (event: any) => {
    setSelectedTitle(event.value);
    setSelectedChapter(null);

    setChaptersLoading(true);
    await dispatch(GetByTitleId(event.value.id));

    if (chapterCache[event.value.id]) {
      dispatch(setChaptersForTitle({ titleId: event.value.id, chapters: chapterCache[event.value.id] }));
    } else {
      await dispatch(getChaptersByTitleId(event.value.id));
    }

    setChaptersLoading(false);
  };

  const handleAgencySelectChange = (event: any) => {
    setSelectedAgency(event.value);
    setSelectedTitle(null);
    setSelectedChapter(null);
  };

  const triggerTitleListUpdate = (event: any) => {    
    const promise = dispatch(getTitleByAgencyId(event.value.id));
  }
  //#endregion

  //#region chapter functions
  // gets set on a chapter being selected to edit

  // switches to the edit view
  const getTitleChapters = async (titleId?: number) => {
    if (titleId) {
      let chapters = await chapterAPI.getChaptersByTitleId(titleId);
      setTitleData(chapters);
    }
  };

  const toggleTitleDialog = async () => {
    setChaptersLoading(true);
    setTitleData([]);
    titleDialogOpen ? setTitleDialogOpen(false) : setTitleDialogOpen(true);
    if (selectedTitle) {
      await getTitleChapters(selectedTitle.id);
    }
    setChaptersLoading(false);
  };

  const toggleChapterDialog = async (chapter?: any) => {
    chapterDialogOpen ? setChapterDialogOpen(false) : setChapterDialogOpen(true);
    if (chapter) {
      setSelectedChapter(chapter);
    }
  };

  // #region PDF export
  const exportPDF = () => {
    if (pdfExport.current) {
      pdfExport.current.save();
    }
  };

  const [isAppUser, setIsAppUser] = useState<boolean>(false);

  useEffect(() => {
    let userRoles = JSON.parse(sessionStorage.getItem('roles') ?? "[]");
    setIsAppUser(!!userRoles?.includes(Roles.AppUser));
  }, [])

  const baseUrl = new APISettings().getApiURL();

  const downloadPdf = async (containerName: string, blobName: string, titleId?: number, chapterId?:number) => {
    let url = chapterId
      ? `${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${blobName}/${chapterId}`
      : `${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${blobName}`;

    if (titleId && !await titleAPI.titlePdfExists(titleId))
    {
      setGeneratingTitle(true);
      await titleAPI.createTitlePdf(titleId).finally(() => {setGeneratingTitle(false)});
    }

    let blob: Blob = await fetch(url).then((r) => r.blob());

    saveAs(blob, blobName);
  }

  const downloadPdfNoCheck = async (containerName: string, blobName: string) => {
    let url = `${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${blobName}`;
    let blob: Blob = await fetch(url).then((r) => r.blob());

    saveAs(blob, blobName);
  }

  const [generatingTitle, setGeneratingTitle] = useState<boolean>(false);
  // #endregion

  //#region history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);

  const toggleHistoryDialog = (chapter?: any) => {
    if (chapter) {
      setSelectedChapter(chapter);
    }

    setHistoryDialogOpen(!historyDialogOpen);
  }


  //#endregion

  //#region emergency regulation view

  const [emergencyRegulationOpen, setEmergencyRegulationOpen] = useState<boolean>(false);

  const toggleEmergencyRegulation = (emergencyRegulationId?: number) => {
    !emergencyRegulationOpen && emergencyRegulationId && getEmergencyRegulation(emergencyRegulationId);
    emergencyRegulationOpen ? setEmergencyRegulationOpen(false) : setEmergencyRegulationOpen(true);
    !emergencyRegulationOpen && setRenew(false);
  }

  const [emergencyRegulation, setEmergencyRegulation] = useState<any>();

  const getEmergencyRegulation = async (emergencyRegulationId: number) => {
    setEmergencyRegulation(await emergencyRegulationApi.getById(emergencyRegulationId));
  }

  const [renew, setRenew] = useState<boolean>(false);

  const toggleRenew = () => {
    setRenew(!renew);
  }

  const [renewDate, setRenewDate] = useState<Date | null>();

  const handleRenewDateChange = (date: Date | null) => {
    setRenewDate(date);
  }

  const [renewed, setRenewed] = useState<boolean>(false);

  const confirmRenewDate = () => {
    emergencyRegulationApi.renewEmergencyRegulation({
      emergencyRegulationId: emergencyRegulation.id,
      renewalDate: renewDate
    }).then(() => {
      setRenewed(true);
    });
  }

  //#endregion

  //#region subscribing
  const { addNotification } = useNotificationHelper();

  const [subLoggedIn, setSubLoggedIn] = useState<boolean>(false);

  useEffect(() => {
    const runningInEffect = async () => {
      if (userSubscriptions.length < 1 && !!subscriptionUser && !!subscriptionEmail && subscriptionEmail !== "") {
        await dispatch(getUserSubscriptionByEmail(subscriptionEmail));
      }
    }

    let subToken = localStorage.getItem("subscriptionToken");
    if (!subLoggedIn && !!subscriptionUser && !!subToken) {
      setSubLoggedIn(true);
      runningInEffect().catch(x => console.log(x));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subLoggedIn, subscriptionEmail, userSubscriptions]);


  const subscribeToAgency = async (id: number)=> {
    let noChildSubscriptions: boolean = true;

    userSubscriptions.forEach((x: UserSubscription) => {

      if(x.agencyId === selectedAgency.id && (x.titleId || x.chapterId)){
        noChildSubscriptions = false;

        let childObj = {};

        if(x.titleId){
          childObj = {
            name: x.titleName,
            type: "type",
            id: x.titleId,
          };
        }

        if(x.chapterId){
          childObj = {
            name: x.chapterName,
            type: "chapter",
            id: x.chapterId,
          };
        }

        listOfChildrenSubscriptionsForUserNotification.push(childObj);
      }
    });

    if(!noChildSubscriptions){
      const parentObj = {
        id: id,
        type: "agency",
      }

      setParentSubscription(parentObj);

      toggleParentSubscriptionConfirm();
    }else{
      await subscribe(id, "agency");
    }
  }

  const subscribeToTitle = async (id : number) => {

    let noChildSubscriptions: boolean = true;

    let noParentSubscriptions: boolean = true;

    userSubscriptions.forEach((x: UserSubscription) => {

      if (x.agencyId === selectedAgency.id && !x.titleId && !x.chapterId) {

        noParentSubscriptions = false;

        setConfirmDialogInfo({
          parentSubName: x.agencyName ?? "",
          parentSubType: "Agency",
          newSubId: id,
          newSubType: "title",
        });

        const childObject = {
          subscriptionUserId: subscriptionUserId,
          titleId: selectedTitle.id,
        };

        setParentSubscription(x);
        setChildSubscription(childObject);
      }

      else if(x.titleId === selectedTitle.id && x.chapterId){

        noChildSubscriptions = false;

        const childObj = {
          name: x.chapterName,
          type: "chapter",
          id: x.chapterId,
        };

        listOfChildrenSubscriptionsForUserNotification.push(childObj);
      }

    });

    if(!noParentSubscriptions){
      toggleSubConfirm();
    }else if(!noChildSubscriptions){
      const parentObj = {
        id: id,
        type: "title",
      }

      setParentSubscription(parentObj);

      toggleParentSubscriptionConfirm();
    }else{
      await subscribe(id, "title");
    }

  }

  const subscribeToChapter = async (id : number) => {

    let noParentSubscriptions = true;

    userSubscriptions.forEach((x : UserSubscription) => {

      if(x.titleId === selectedTitle.id && !x.chapterId){

        noParentSubscriptions = false;

        setConfirmDialogInfo({
          parentSubName: selectedTitle.titleName,
          parentSubType: "Title",
          newSubId: id,
          newSubType: "chapter",
        });

        const childObject = {
          subscriptionUserId: subscriptionUserId,
          chapterId: id,
        };

        setParentSubscription(x);
        setChildSubscription(childObject);

      }else if (x.agencyId === selectedAgency.id && !x.titleId){

        noParentSubscriptions = false;

        setConfirmDialogInfo({
          parentSubName: x.agencyName ?? "",
          parentSubType: "Agency",
          newSubId: id,
          newSubType: "chapter",
        });

        const childObject = {
          subscriptionUserId: subscriptionUserId,
          chapterId: id,
        };

        setParentSubscription(x);
        setChildSubscription(childObject);
      }
    })

    if(!noParentSubscriptions){
      toggleSubConfirm();
    }else{
      await subscribe(id, "chapter");
    }
  }

  const subscribe = async (
      id: number,
      type: "agency" | "title" | "chapter",
      ) => {
    let newSub: any = { subscriptionUserId: subscriptionUserId};

    newSub[type.concat("Id")] = id;

    userSubscriptionAPI.createUserSubscription(newSub).then((response) => {
      addNotification({
        type: 'success',
        message: `Subscription added successfully.`,
      });

      let subEmail = localStorage.getItem("subscriptionEmail");
      if (!!subscriptionUser && !!subEmail) {
        dispatch(getUserSubscriptionByEmail(subEmail));
        dispatch(clearUserSubscriptionTableData());
      }
    }).catch(() => {
      addNotification({
        type: 'error',
        message: `Failed to add subscription.`
      });
    });
  }

  const unsubscribeParentSubscribeChild = async () => {

    const newSubscription = {
      ...childSubscription,
      subscriptionUserId: subscriptionUserId,
    }

    const previousSubscription = {
      ...parentSubscription,
      subscriptionUserId: subscriptionUserId,
    };

    dispatch(deleteUserSubscription(previousSubscription)).then(() => {

      userSubscriptionAPI.createUserSubscription(newSubscription).then((response) => {
        addNotification({
          type: 'success',
          message: `Subscription added successfully.`,
        });

        if(previousSubscription.type === "agency"){
          dispatch(removeUserAgencySubscription(previousSubscription));
        }else if(previousSubscription.type === "title"){
          dispatch(removeUserTitleSubscription(previousSubscription));
        }


        toggleSubConfirm();

        let subEmail = localStorage.getItem("subscriptionEmail");
        if (!!subscriptionUser && !!subEmail) {
          dispatch(getUserSubscriptionByEmail(subEmail));
          dispatch(clearUserSubscriptionTableData());
        }

        setParentSubscription({});
        setChildSubscription({});
      }).catch(() => {
        addNotification({
          type: 'error',
          message: `Failed to add subscription.`
        });
      });
    });
  }

  const unsubscribeChildrenSubscribeParent = async () => {

    const newSubscription = {
      ...parentSubscription,
      subscriptionUserId: subscriptionUserId,
    }

    newSubscription[newSubscription.type.concat("Id")] = newSubscription.id;

    delete newSubscription["id"];

    userSubscriptionAPI.createUserSubscription(newSubscription).then(async () => {
      addNotification({
        type: 'success',
        message: `Subscription added successfully.`,
      });

      listOfChildrenSubscriptionsForUserNotification.forEach( (subscription: any) => {
        if(subscription.type === "title"){
          dispatch(removeUserTitleSubscription(subscription));
        }else if(newSubscription.type === "chapter"){
          dispatch(removeUserChapterSubscription(subscription))
        }
      });

      await dispatch(getUserSubscriptionByEmail(localStorage.getItem("subscriptionEmail") ?? ""));

      toggleParentSubscriptionConfirm();
      setParentSubscription({});
      setChildSubscription({});
    }).catch(() => {
      addNotification({
        type: 'error',
        message: `Failed to add subscription.`
      });
    });
  }

  const [confirmDialogInfo, setConfirmDialogInfo] = useState<{
    parentSubName: string,
    parentSubType: string,
    newSubId: number,
    newSubType: "agency" | "title" | "chapter",
  }>();

  const [isSubConfirmOpen, setIsSubConfirmOpen] = useState<boolean>(false);
  const [isParentSubscriptionOpen, setIsParentSubscriptionOpen] = useState<boolean>(false);

  const toggleSubConfirm = () => {
    if (isSubConfirmOpen) {
      setConfirmDialogInfo(undefined);
    }
    setIsSubConfirmOpen(!isSubConfirmOpen);
  }

  const toggleParentSubscriptionConfirm = () => {
    if (isParentSubscriptionOpen) {
      setListOfChildrenSubscriptionsForUserNotification([]);
    }
    setIsParentSubscriptionOpen(!isParentSubscriptionOpen);
  }

  const subExists = (id: number, type: 'agency' | 'title' | 'chapter') => {
    if (!userSubscriptions) {
      return false;
    }
    switch (type) {
      case 'agency':
        return userSubscriptions.some((x: UserSubscription) => x.agencyId && x.agencyId === id && !x.titleId && !x.chapterId);
      case 'title':
        return userSubscriptions.some((x: UserSubscription) => x.titleId && x.titleId === id && !x.chapterId);
      case 'chapter':
        return userSubscriptions.some((x: UserSubscription) => x.chapterId && x.chapterId === id);
      default:
        return false;
    }
  }

  //#endregion

  //#region pdf regeneration

  const [isSosUser, setIsSosUser] = useState<boolean>(false);
  const authRoles: any = useAppSelector((state) => state.authRoles.roles);

  useEffect(() => {
    dispatch(validateUserRoles());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAppUser && (authRoles.includes("Admin") || authRoles.includes("SOS Office"))) {
      setIsSosUser(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppUser])

  const [recreatingChapters, setRecreatingChapters] = useState<any>([]);

  const recreateChapterPdf = async (id: any) => {
    setRecreatingChapters([...recreatingChapters, id]);
    let blobName = await chapterAPI.recreatePdf(id);
    setRecreatingChapters(recreatingChapters.splice(recreatingChapters.indexOf(id)));
    downloadPdfNoCheck('chapter-pdfs', blobName);
  }

  const [recreatingTitle, setRecreatingTitle] = useState<boolean>(false);

  const recreateTitlePdf = async (id: any) => {
    setRecreatingTitle(true);
    let blobName = await titleAPI.recreatePdf(id);
    setRecreatingTitle(false);
    downloadPdfNoCheck('title-pdfs', blobName);
  }

  //#endregion

  return {
    // functions
    handleTitleSelectChange,
    handleAgencySelectChange,
    triggerTitleListUpdate,
    toggleTitleDialog,
    toggleChapterDialog,
    toggleHistoryDialog,
    exportPDF,
    setDefaultValues,
    downloadPdf,
    toggleEmergencyRegulation,
    toggleRenew,
    handleRenewDateChange,
    confirmRenewDate,
    toggleSubConfirm,
    toggleParentSubscriptionConfirm,
    subExists,
    unsubscribeParentSubscribeChild,
    unsubscribeChildrenSubscribeParent,
    subscribeToAgency,
    subscribeToTitle,
    subscribeToChapter,
    recreateChapterPdf,
    recreateTitlePdf,
    // redux - these are only here because of the useEffect
    getAllAgencies,
    // state
    titleValues,
    titleList,
    chapterValues,
    agencyValues,
    agencyTitleValues,
    dispatch,
    selectedAgency,
    selectedTitle,
    selectedChapter,
    chapterDialogOpen,
    historyDialogOpen,
    chaptersLoading,
    titleDialogOpen,
    titleData,
    pdfExport,
    isAppUser,
    emergencyRegulation,
    emergencyRegulationOpen,
    renew,
    renewed,
    renewDate,
    subLoggedIn,
    isSubConfirmOpen,
    isParentSubscriptionOpen,
    confirmDialogInfo,
    generatingTitle,
    userSubscriptions,
    listOfChildrenSubscriptionsForUserNotification,
    recreatingChapters,
    recreatingTitle,
    isSosUser,
  };
};
